"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import type {
  PublicActivityDTO,
  PublicAvailabilityUnitDTO,
  PublicBookingResponseDTO,
  PublicBookingSiteDTO,
  PublicFoodItemDTO,
  PublicQuoteDTO,
  PublicStayPackageDTO,
  CheckoutAddonDTO,
  BookingSiteSection,
} from "@/lib/types";
import { formatMoney, fromMinorUnits } from "@/lib/money";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { localizeError } from "@/i18n/errors";
import { DateRangePicker, GuestStepper } from "@/components/DateRangePicker";
import { SiteSupportButton } from "@/components/SiteSupportButton";
import { useTracking, type FunnelData } from "@/components/TrackingProvider";
import { storedUtm } from "@/lib/tracking/browser";
import { SiteChatWidget } from "@/components/SiteChatWidget";
import { PublicActivityBookingModal } from "@/components/PublicActivityBookingModal";
import { themeFromSettings } from "@/lib/booking-site-themes";
import "./public-site.css";

interface PublicBookingSiteViewProps {
  site: PublicBookingSiteDTO;
  units: PublicAvailabilityUnitDTO[];
  activities?: PublicActivityDTO[];
  food?: PublicFoodItemDTO[];
  packages?: PublicStayPackageDTO[];
  /** Extras a guest can add in the booking form (firewood, breakfast...). */
  addons?: CheckoutAddonDTO[];
  isPreview?: boolean;
  /** Staff preview only: where the "back to editor" link in the preview bar goes. */
  backHref?: string;
  /** Staff preview only: loads units for a draft site (the public endpoint hides unpublished sites). */
  loadUnits?: (dates: { check_in: string; check_out: string }) => Promise<PublicAvailabilityUnitDTO[] | null>;
}

/** Only render images served from our own uploads or an explicit http(s) URL. */
export function safeImageUrl(url: unknown): string {
  if (typeof url !== "string") return "";
  const u = url.trim();
  return u.startsWith("/uploads/") || /^https?:\/\//i.test(u) ? u : "";
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

type UnitMedia = { kind: "image" | "video"; src: string };

/** Swipeable strip for a unit: primary image, then its gallery, then its videos. Tap the expand button for fullscreen. */
function UnitPhotos({ images, videos = [], name, alt }: { images: string[]; videos?: string[]; name: string; alt?: string }) {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const [full, setFull] = useState(false);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const touchX = React.useRef<number | null>(null);
  const list: UnitMedia[] = [
    ...images.map(safeImageUrl).filter(Boolean).map((src): UnitMedia => ({ kind: "image", src })),
    ...videos.map(safeImageUrl).filter(Boolean).map((src): UnitMedia => ({ kind: "video", src })),
  ];
  const count = list.length;
  const idx = Math.min(i, Math.max(count - 1, 0));
  const go = (d: number) => setI((idx + d + count) % count);

  // Fullscreen is a fixed-position overlay (works on iOS, where only <video> can use the Fullscreen API) that also
  // asks the browser for real fullscreen where supported. Esc / the browser's own exit both close it.
  const setFullscreen = (on: boolean) => {
    setFull(on);
    const el = boxRef.current;
    if (!el) return;
    if (on) void el.requestFullscreen?.().catch(() => undefined);
    else if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  };
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    const onFsChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, idx, count]);

  if (count === 0) return <div className="ps-photos-empty">{name}</div>;
  const cur = list[idx];
  return (
    <div
      ref={boxRef}
      className={`ps-photos${full ? " is-full" : ""}`}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        if (start === null || count < 2) return;
        const dx = e.changedTouches[0].clientX - start;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      {cur.kind === "image" ? (
        <img key={cur.src} src={cur.src} alt={alt || name} loading="lazy" />
      ) : (
        // key resets playback when sliding away from a video
        <video key={cur.src} src={cur.src} controls playsInline preload="metadata" aria-label={t("publicsite.booking.unit.video")} />
      )}
      <button type="button" className="ps-photos-full" aria-label={t(full ? "publicsite.booking.unit.exitFullscreen" : "publicsite.booking.unit.fullscreen")} onClick={() => setFullscreen(!full)}>
        {full ? "✕" : "⛶"}
      </button>
      {count > 1 && (
        <>
          <button type="button" className="ps-photos-nav prev" aria-label={t("publicsite.booking.unit.prev")} onClick={() => go(-1)}>{"‹"}</button>
          <button type="button" className="ps-photos-nav next" aria-label={t("publicsite.booking.unit.next")} onClick={() => go(1)}>{"›"}</button>
          <div className={`ps-photos-dots${cur.kind === "video" ? " is-video" : ""}`} aria-label={t("publicsite.booking.unit.photoOf", { n: idx + 1, total: count })}>
            {list.map((m, k) => <i key={k} className={`${k === idx ? "on" : ""}${m.kind === "video" ? " vid" : ""}`} />)}
          </div>
        </>
      )}
    </div>
  );
}

function osmEmbedUrl(lat: number, lng: number): string {
  const d = 0.006;
  const bbox = [lng - d, lat - d, lng + d, lat + d].map((n) => n.toFixed(6)).join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)}%2C${lng.toFixed(6)}`;
}

export function PublicBookingSiteView({
  site,
  units: initialUnits,
  activities = [],
  food = [],
  packages = [],
  addons = [],
  isPreview = false,
  backHref,
  loadUnits,
}: PublicBookingSiteViewProps) {
  const { t, tx, locale, formatDate } = useI18n();
  const tracking = useTracking();
  const [unitsList, setUnitsList] = useState<PublicAvailabilityUnitDTO[]>(initialUnits);
  const [selectedUnit, setSelectedUnit] = useState<PublicAvailabilityUnitDTO | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<"DETAILS" | "REVIEW">("DETAILS");

  // Form State
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  /** Plots of a unit type booked together (pool cards only). */
  const [plotCount, setPlotCount] = useState(1);
  // Children are counted in the guests (capacity and per-guest extras use the total).
  const guestCount = (adults || 0) + (children || 0);
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<PublicBookingResponseDTO | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  /** The code the guest applied; the server validates it and prices the stay with it. */
  const [voucherCode, setVoucherCode] = useState("");
  const [quote, setQuote] = useState<(PublicQuoteDTO & { key: string }) | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [detailUnit, setDetailUnit] = useState<PublicAvailabilityUnitDTO | null>(null);
  const [dayTripActivity, setDayTripActivity] = useState<PublicActivityDTO | null>(null);
  /** Extras picked in the form, with the quantity (only QUANTITY extras let the guest choose it). */
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  /** Unit list: order (as set by the owner, or by price) and whether every available unit is shown. */
  const [unitSort, setUnitSort] = useState<"DEFAULT" | "PRICE_ASC" | "PRICE_DESC">("DEFAULT");
  const [showAllUnits, setShowAllUnits] = useState(false);

  // Default dates: tomorrow to +2 days
  useEffect(() => {
    const today = new Date();
    const d1 = new Date(today);
    d1.setDate(today.getDate() + 1);
    const d2 = new Date(today);
    d2.setDate(today.getDate() + 3);

    const fmt = (d: Date) => d.toISOString().split("T")[0];
    setCheckIn(fmt(d1));
    setCheckOut(fmt(d2));
  }, []);

  const calculateNights = useCallback(() => {
    if (!checkIn || !checkOut) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);

  const nights = calculateNights();
  const packagesFor = useCallback(
    (unitId: string) => packages.filter((p) => p.unit_ids.length === 0 || p.unit_ids.includes(unitId)),
    [packages]
  );
  const selectedPackage = packages.find((p) => p.id === selectedPackageId) || null;
  // Units of an extra for this stay; the server counts the same way.
  const addonUnits = (a: CheckoutAddonDTO, qty: number) =>
    a.basis === "PER_GUEST" ? guestCount : a.basis === "PER_NIGHT" ? nights : a.basis === "PER_GUEST_NIGHT" ? guestCount * nights : qty;
  const pickedAddons = addons.filter((a) => addonQty[a.id] !== undefined);
  const addonsParam = pickedAddons.map((a) => `${a.id}:${addonQty[a.id]}`).join(",");
  const localAddonsTotal = pickedAddons.reduce((sum, a) => sum + a.price * addonUnits(a, addonQty[a.id]), 0);
  const plots = selectedUnit?.kind === "POOL" ? Math.max(1, plotCount || 1) : 1;
  const localTotal = selectedUnit ? (selectedPackage ? selectedPackage.price : (selectedUnit.total_price ?? nights * selectedUnit.base_price) * plots) + localAddonsTotal : 0;
  // The server's quote is authoritative; the local figure only fills the gap while it loads (and in staff preview).
  const quoteKey = [selectedUnit?.id, checkIn, checkOut, selectedPackageId, guestCount, children, plots, voucherCode, addonsParam].join("|");
  const freshQuote = quote && quote.key === quoteKey ? quote : null;
  const displayTotal = freshQuote ? freshQuote.total : localTotal;

  // Booking-funnel analytics (GA4 / Meta). Never from the staff preview.
  const track = (event: "view_item" | "search" | "begin_checkout" | "add_payment_info" | "generate_lead", data?: FunnelData) => {
    if (!isPreview) tracking.track(event, data);
  };
  const unitItem = (u: PublicAvailabilityUnitDTO, amount: number) => {
    const currency = u.currency || site.property.currency;
    return { value: fromMinorUnits(amount, currency), currency, items: [{ item_id: u.id, item_name: u.name, price: fromMinorUnits(amount, currency) }] };
  };
  useEffect(() => {
    if (detailUnit) track("view_item", unitItem(detailUnit, detailUnit.total_price ?? detailUnit.base_price));
    // Once per opened unit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailUnit?.id]);
  // A guest changing the dates is a search (the first dates are the page's defaults, not theirs).
  const datesSeen = useRef(0);
  useEffect(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return;
    if (datesSeen.current++ === 0) return;
    const timer = setTimeout(() => track("search", { search_term: `${checkIn}..${checkOut}` }), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut]);

  // Refresh availability when dates change
  const refreshAvailability = useCallback(async () => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return;
    try {
      if (loadUnits) {
        const list = await loadUnits({ check_in: checkIn, check_out: checkOut });
        if (list) setUnitsList(list);
        return;
      }
      const res = await fetch(
        `/api/public/sites/units?hostname=${encodeURIComponent(site.hostname)}&check_in=${checkIn}&check_out=${checkOut}`
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setUnitsList(data.data);
      }
    } catch {
      // Keep existing list on failure
    }
  }, [checkIn, checkOut, site.hostname, loadUnits]);

  useEffect(() => {
    if (checkIn && checkOut && checkOut > checkIn) {
      refreshAvailability();
    }
  }, [checkIn, checkOut, refreshAvailability]);

  // A package fixes the length of stay: check-out follows check-in.
  useEffect(() => {
    if (selectedPackage && checkIn) setCheckOut(addDays(checkIn, selectedPackage.nights));
  }, [selectedPackage, checkIn]);

  // Live, server-side price and availability for the reservation form (debounced).
  useEffect(() => {
    if (!isBookingModalOpen || !selectedUnit || isPreview || nights <= 0 || bookingSuccess) {
      setQuote(null);
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const qs = new URLSearchParams({ hostname: site.hostname, unit_id: selectedUnit.id, check_in: checkIn, check_out: checkOut, guests: String(guestCount), children: String(children || 0), units: String(plots) });
        if (selectedPackageId) qs.set("package_id", selectedPackageId);
        if (voucherCode) qs.set("voucher", voucherCode);
        if (addonsParam) qs.set("addons", addonsParam);
        const res = await fetch(`/api/public/sites/quote?${qs.toString()}`, { signal: ctl.signal });
        const data = await res.json();
        if (data.success) setQuote({ ...data.data, key: [selectedUnit.id, checkIn, checkOut, selectedPackageId, guestCount, children, plots, voucherCode, addonsParam].join("|") });
      } catch {
        // Aborted or offline: keep the last quote; the booking itself is re-checked on submit.
      } finally {
        setQuoteLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [isBookingModalOpen, selectedUnit, isPreview, nights, bookingSuccess, site.hostname, checkIn, checkOut, guestCount, children, plots, selectedPackageId, voucherCode, addonsParam]);

  const handleOpenPackage = (pkg: PublicStayPackageDTO) => {
    const eligible = unitsList.filter((u) => pkg.unit_ids.length === 0 || pkg.unit_ids.includes(u.id));
    const unit = eligible.find((u) => u.available !== false) || eligible[0];
    if (!unit) return;
    handleOpenBooking(unit, pkg.id);
  };

  const handleOpenBooking = (unit: PublicAvailabilityUnitDTO, packageId = "") => {
    if (detailUnit?.id !== unit.id) track("view_item", unitItem(unit, unit.total_price ?? unit.base_price));
    setDetailUnit(null);
    setQuote(null);
    setSelectedPackageId(packageId);
    setVoucherInput("");
    setVoucherCode("");
    setAddonQty({});
    setSelectedUnit(unit);
    setAdults(Math.min(2, unit.capacity));
    setChildren(0);
    setPlotCount(1);
    setBookingError(null);
    setBookingSuccess(null);
    setCheckoutStep("DETAILS");
    setIsBookingModalOpen(true);
  };

  const alternatives = selectedUnit
    ? unitsList.filter(
        (u) =>
          u.id !== selectedUnit.id &&
          u.available !== false &&
          u.capacity >= guestCount &&
          (!selectedPackageId || packagesFor(u.id).some((p) => p.id === selectedPackageId))
      )
    : [];

  const switchUnit = (u: PublicAvailabilityUnitDTO) => {
    setSelectedUnit(u);
    if (selectedPackageId && !packagesFor(u.id).some((p) => p.id === selectedPackageId)) setSelectedPackageId("");
    setBookingError(null);
  };

  const handleProceedToReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUnit) return;
    if (nights <= 0) {
      setBookingError(t("publicsite.booking.errors.dateOrder"));
      return;
    }
    if (!guestName.trim()) {
      setBookingError(t("publicsite.booking.errors.nameRequired"));
      return;
    }
    if (!guestPhone.trim()) {
      setBookingError(t("publicsite.booking.errors.phoneRequired"));
      return;
    }
    if (!/^\+?[\d\s().-]{8,}$/.test(guestPhone.trim())) {
      setBookingError(t("publicsite.booking.errors.phoneInvalid"));
      return;
    }
    // Email is optional; when given it must be a real address (the summary and PDFs are sent there).
    if (guestEmail.trim() && !/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(guestEmail.trim())) {
      setBookingError(t("publicsite.booking.errors.emailInvalid"));
      return;
    }
    setBookingError(null);
    setCheckoutStep("REVIEW");
    track("begin_checkout", unitItem(selectedUnit, displayTotal));
  };

  const handlePayNow = async () => {
    if (!bookingSuccess) return;
    if (selectedUnit) track("add_payment_info", unitItem(selectedUnit, displayTotal));
    try {
      setPayLoading(true);
      setPayError(null);
      const res = await fetch("/api/public/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_reference: bookingSuccess.public_reference, access_token: bookingSuccess.access_token }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setPayError(data.error ? localizeError(locale, data.error) : t("publicsite.booking.success.payFailed"));
    } catch {
      setPayError(t("publicsite.booking.success.payFailed"));
    } finally {
      setPayLoading(false);
    }
  };

  const handleBookingSubmit = async () => {
    if (!selectedUnit) return;
    if (isPreview) {
      setBookingError(t("publicsite.booking.errors.previewOnly"));
      return;
    }
    try {
      setBookingLoading(true);
      setBookingError(null);

      const idempotencyKey = `bkg_idem_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      const res = await fetch(`/api/public/sites/bookings?hostname=${encodeURIComponent(site.hostname)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          unit_id: selectedUnit.id,
          guest_name: guestName.trim(),
          guest_phone: guestPhone.trim(),
          guest_email: guestEmail.trim() || undefined,
          check_in: checkIn,
          check_out: checkOut,
          guest_count: guestCount,
          child_count: children || 0,
          unit_count: plots,
          notes: notes.trim() || undefined,
          package_id: selectedPackageId || undefined,
          voucher_code: voucherCode || undefined,
          addons: pickedAddons.map((a) => ({ catalog_item_id: a.id, quantity: addonQty[a.id] })),
          // The campaign the guest arrived with (utm_* on the first page of their visit).
          ...storedUtm(),
        }),
      });

      const data = await res.json();
      if (data.success && data.data) {
        setBookingSuccess(data.data);
        // Without online payment the booking is a request the owner confirms: a lead, not (yet) a purchase.
        if (!site.online_payment_enabled) track("generate_lead", { lead_type: "booking_request", ...unitItem(selectedUnit, displayTotal) });
        refreshAvailability();
      } else {
        const msg = String(data.error || "");
        if (msg.includes("CONFLICTING_RESERVATION") || msg.includes("OVERLAPPING_RESERVATION")) {
          // Someone else took these dates between the quote and the submit: say so, refresh, and let them pick again.
          setBookingError(t("publicsite.booking.errors.unavailable"));
          setCheckoutStep("DETAILS");
          refreshAvailability();
        } else {
          setBookingError(data.error ? localizeError(locale, data.error) : t("publicsite.booking.errors.createFailed"));
        }
      }
    } catch {
      setBookingError(t("publicsite.booking.errors.submitFailed"));
    } finally {
      setBookingLoading(false);
    }
  };

  const accent = /^#[0-9a-f]{3,8}$/i.test(site.primary_color || "") ? (site.primary_color as string) : undefined;
  const heroUrl = safeImageUrl(site.hero_url);
  const logoUrl = safeImageUrl(site.logo_url);
  const money = (n: number, cur?: string) => formatMoney(n, cur || site.property.currency);
  // The guest stepper goes up to the largest party the property can host in one booking.
  const maxGuests = Math.max(1, ...unitsList.map((u) => (u.kind === "POOL" ? u.capacity * Math.max(1, u.total_count ?? 1) : u.capacity)));
  // A unit type (pool of plots): how many are left for the dates, or how many there are.
  const poolNote = (u: PublicAvailabilityUnitDTO) =>
    u.kind !== "POOL"
      ? null
      : u.available_count !== undefined
        ? t("publicsite.booking.units.poolLeft", { count: u.available_count, total: u.total_count ?? 0 })
        : t("publicsite.booking.units.poolTotal", { count: u.total_count ?? 0 });
  // Units priced per person: what each guest over the included ones adds per night.
  const perGuestNote = (u: PublicAvailabilityUnitDTO) =>
    u.included_guests === null || u.included_guests === undefined
      ? null
      : (u.included_guests === 0
          ? t("publicsite.booking.units.perPerson", { amount: money(u.extra_guest_price, u.currency) })
          : t("publicsite.booking.units.perGuest", { amount: money(u.extra_guest_price, u.currency), count: u.included_guests })) +
        (u.child_price !== null && u.child_price !== undefined ? ` · ${t("publicsite.booking.units.childPrice", { amount: money(u.child_price, u.currency) })}` : "");
  const configured = site.sections.filter((sec) => sec.enabled && sec.type !== "HERO");
  // Sites created before packages existed have no PACKAGES section: show one after the units when packages exist.
  const sections: BookingSiteSection[] =
    packages.length > 0 && !configured.some((sec) => sec.type === "PACKAGES")
      ? configured.flatMap((sec) =>
          sec.type === "UNITS"
            ? [sec, { id: "sec_packages_auto", type: "PACKAGES", enabled: true, title: t("publicsite.booking.packages.title"), subtitle: t("publicsite.booking.packages.subtitle") } as BookingSiteSection]
            : [sec]
        )
      : configured;
  const has = (type: string) => sections.some((sec) => sec.type === type && (type !== "ACTIVITIES" || activities.length > 0));

  // A unit fits the party when its capacity (a type: across the plots still free) takes everyone.
  const fitsParty = (u: PublicAvailabilityUnitDTO) =>
    (u.kind === "POOL" ? u.capacity * Math.max(1, u.available_count ?? u.total_count ?? 1) : u.capacity) >= guestCount;
  const stayPrice = (u: PublicAvailabilityUnitDTO) => u.total_price || (nights > 0 ? nights * u.base_price : u.base_price);
  const unitPhoto = (u: PublicAvailabilityUnitDTO) => safeImageUrl((u.images && u.images[0]) || u.primary_image_url);
  const INITIAL_UNITS = 6;

  const renderUnitCard = (unit: PublicAvailabilityUnitDTO, bookable: boolean) => {
    const photo = unitPhoto(unit);
    const openDetail = () => setDetailUnit(unit);
    return (
      <div
        key={unit.id}
        className={`ps-card ps-room${bookable ? "" : " is-booked"}`}
        role="button"
        tabIndex={0}
        aria-label={t("publicsite.booking.units.openDetails", { name: unit.name })}
        onClick={openDetail}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDetail();
          }
        }}
      >
        <div className="ps-card-media">
          {photo ? <img src={photo} alt={unit.name} loading="lazy" /> : <span className="ps-photos-empty">{unit.name}</span>}
          {unit.images && unit.images.length > 1 && <span className="ps-room-count">{t("publicsite.booking.units.photos", { count: unit.images.length })}</span>}
        </div>
        <div className="ps-card-body">
          <div>
            <div className="ps-card-title">
              <h3>{unit.name}</h3>
              <span className="ps-tag">{unit.type}</span>
            </div>
            <p className="ps-muted ps-room-meta">
              {t("publicsite.booking.units.capacity", { count: unit.capacity })}
              {poolNote(unit) && <> · {poolNote(unit)}</>}
            </p>
            {perGuestNote(unit) && <p className="ps-muted ps-room-extra">{perGuestNote(unit)}</p>}
            {unit.amenities && unit.amenities.length > 0 && (
              <div className="ps-chips ps-room-chips">
                {unit.amenities.slice(0, 3).map((a) => <span key={a} className="ps-chip">{a}</span>)}
                {unit.amenities.length > 3 && <span className="ps-chip">{`+${unit.amenities.length - 3}`}</span>}
              </div>
            )}
          </div>
          <div className="ps-card-foot">
            <div className="ps-room-price">
              <span className="ps-room-rate">
                <span className="ps-price">{money(unit.base_price, unit.currency)}</span>
                <span className="ps-muted"> {t("publicsite.booking.units.perNight")}</span>
              </span>
              {nights > 0 && bookable && (
                <div className="ps-muted">{t("publicsite.booking.units.totalNights", { amount: money(stayPrice(unit), unit.currency), nights: t("publicsite.booking.nights", { count: nights }) })}</div>
              )}
            </div>
            {bookable ? (
              <button
                type="button"
                className="ps-btn ps-btn-primary ps-btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenBooking(unit);
                }}
              >
                {t("publicsite.booking.units.choose")}
              </button>
            ) : (
              <span className="ps-avail no">{unit.available === false ? t("publicsite.booking.units.booked") : t("publicsite.booking.units.tooSmall", { count: unit.capacity })}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUnits = () => {
    if (unitsList.length === 0) return <div className="ps-empty">{t("publicsite.booking.units.empty")}</div>;
    const ordered =
      unitSort === "DEFAULT" ? unitsList : [...unitsList].sort((a, b) => (stayPrice(a) - stayPrice(b)) * (unitSort === "PRICE_ASC" ? 1 : -1));
    const open = ordered.filter((u) => u.available !== false && fitsParty(u));
    const closed = ordered.filter((u) => !(u.available !== false && fitsParty(u)));
    const shown = showAllUnits ? open : open.slice(0, INITIAL_UNITS);
    return (
      <>
        <div className="ps-rooms-bar">
          <DateRangePicker
            checkIn={checkIn}
            checkOut={checkOut}
            onChange={(a, b) => { setCheckIn(a); setCheckOut(b); }}
            maxDate={site.property.bookable_until}
            className="ps-rooms-dates"
          />
          <GuestStepper value={guestCount} onChange={(n) => { setAdults(n); setChildren(0); }} max={maxGuests} className="ps-rooms-guests" />
        </div>
        {unitsList.length > 3 && (
          <div className="ps-rooms-sort" role="group" aria-label={t("publicsite.booking.units.sortLabel")}>
            {(["DEFAULT", "PRICE_ASC", "PRICE_DESC"] as const).map((mode) => (
              <button key={mode} type="button" className={`ps-chip-btn${unitSort === mode ? " on" : ""}`} aria-pressed={unitSort === mode} onClick={() => setUnitSort(mode)}>
                {t(`publicsite.booking.units.sort.${mode}`)}
              </button>
            ))}
            <span className="ps-muted ps-rooms-count">{t("publicsite.booking.units.availableCount", { count: open.length })}</span>
          </div>
        )}
        {open.length === 0 ? (
          <div className="ps-empty">{t("publicsite.booking.units.noneAvailable")}</div>
        ) : (
          <div className="ps-grid ps-rooms">{shown.map((u) => renderUnitCard(u, true))}</div>
        )}
        {open.length > shown.length && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button type="button" className="ps-btn ps-btn-ghost" onClick={() => setShowAllUnits(true)}>
              {t("publicsite.booking.units.showAll", { count: open.length })}
            </button>
          </div>
        )}
        {closed.length > 0 && (
          <details className="ps-rooms-closed">
            <summary>{t("publicsite.booking.units.unavailableCount", { count: closed.length })}</summary>
            <div className="ps-grid ps-rooms">{closed.map((u) => renderUnitCard(u, false))}</div>
          </details>
        )}
      </>
    );
  };

  const renderPackages = () => (
    <div className="ps-grid">
      {packages.map((pkg) => {
        const img = safeImageUrl(pkg.image_url);
        const unitNames = pkg.unit_ids.map((id) => unitsList.find((u) => u.id === id)?.name).filter(Boolean).join(", ");
        const bookable = unitsList.some((u) => pkg.unit_ids.length === 0 || pkg.unit_ids.includes(u.id));
        return (
          <div key={pkg.id} className="ps-card">
            {img && (
              <div className="ps-card-media">
                <img src={img} alt={pkg.name} />
              </div>
            )}
            <div className="ps-card-body">
              <div>
                <div className="ps-card-title">
                  <h3>{pkg.name}</h3>
                  <span className="ps-tag">{t("publicsite.booking.packages.nights", { count: pkg.nights })}</span>
                </div>
                {pkg.description && <p className="ps-muted" style={{ marginTop: 4 }}>{pkg.description}</p>}
                {pkg.inclusions.length > 0 && (
                  <>
                    <p className="ps-muted" style={{ marginTop: 10, fontWeight: 600 }}>{t("publicsite.booking.packages.includes")}</p>
                    <ul className="ps-pkg-incl">
                      {pkg.inclusions.map((x) => <li key={x}>{x}</li>)}
                    </ul>
                  </>
                )}
                <p className="ps-muted" style={{ marginTop: 8, fontSize: 12 }}>
                  {t("publicsite.booking.packages.appliesTo", { units: unitNames || t("publicsite.booking.packages.allUnits") })}
                </p>
              </div>
              <div className="ps-card-foot">
                <span className="ps-price">{money(pkg.price, pkg.currency)}<span className="ps-muted"> / {t("publicsite.booking.packages.perPackage")}</span></span>
                <button type="button" disabled={!bookable} onClick={() => handleOpenPackage(pkg)} className="ps-btn ps-btn-primary ps-btn-sm">
                  {t("publicsite.booking.packages.book")}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderCatalog = (items: (PublicActivityDTO | PublicFoodItemDTO)[], bookable = false) => (
    <div className="ps-grid">
      {items.map((it) => {
        const img = safeImageUrl(it.image_url);
        return (
          <div key={it.id} className="ps-card">
            {img && (
              <div className="ps-card-media">
                <img src={img} alt={it.name} />
              </div>
            )}
            <div className="ps-card-body">
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700 }}>{it.name}</h3>
                {it.description && <p className="ps-muted" style={{ marginTop: 4 }}>{it.description}</p>}
              </div>
              <span className="ps-price">{money(it.price, it.currency)}<span className="ps-muted"> / {tx(`catalog.priceUnit.${it.price_unit}`)}</span></span>
              {bookable && !isPreview && (
                <button type="button" className="ps-btn ps-btn-primary ps-btn-sm" style={{ justifySelf: "start" }} onClick={() => setDayTripActivity(it as PublicActivityDTO)}>
                  {t("publicsite.dayTrip.book")}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderSectionBody = (sec: BookingSiteSection) => {
    const sectionImg = safeImageUrl(sec.image_url);
    switch (sec.type) {
      case "UNITS":
        return renderUnits();
      case "PACKAGES":
        return packages.length > 0 ? renderPackages() : null;
      case "LOCATION": {
        const { latitude, longitude, address, name } = site.property;
        const hasCoords = typeof latitude === "number" && typeof longitude === "number";
        const mapsHref = hasCoords
          ? `https://www.google.com/maps?q=${latitude},${longitude}`
          : address
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
            : "";
        return (
          <>
            {sectionImg && (
              <div className="ps-section-image">
                <img src={sectionImg} alt={sec.title || ""} />
              </div>
            )}
            {sec.content && <div className="ps-prose">{sec.content}</div>}
            {(hasCoords || address) && (
              <div className="ps-map">
                {address && <div className="ps-map-addr">{address}</div>}
                {hasCoords && <iframe title={t("publicsite.booking.map.mapOf", { name })} src={osmEmbedUrl(latitude as number, longitude as number)} loading="lazy" referrerPolicy="no-referrer" />}
                {mapsHref && <a className="ps-btn ps-btn-ghost ps-btn-sm" style={{ justifySelf: "start" }} href={mapsHref} target="_blank" rel="noopener noreferrer">{t("publicsite.booking.map.openMaps")}</a>}
              </div>
            )}
          </>
        );
      }
      case "ACTIVITIES":
        return renderCatalog(activities, true);
      case "FOOD":
        return renderCatalog(food);
      case "WHY_DIRECT": {
        const points = (sec.items ?? []).map((it) => String(it.text || "").trim()).filter(Boolean);
        if (points.length === 0) return null;
        return (
          <ul className="ps-why">
            {points.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        );
      }
      case "FAQ":
        return (
          <div className="ps-faq">
            {(sec.items ?? []).map((it, i) => (
              <details key={i}>
                <summary>{String(it.q || "")}</summary>
                <p>{String(it.a || "")}</p>
              </details>
            ))}
          </div>
        );
      case "GALLERY": {
        const images = (sec.items ?? []).map((it) => ({ url: safeImageUrl(it.url), caption: String(it.caption || "") })).filter((i) => i.url);
        if (images.length === 0) return null;
        return (
          <div className="ps-gallery">
            {images.map((im, i) => (
              <figure key={i}>
                <img src={im.url} alt={im.caption || site.property.name} loading="lazy" />
                {im.caption && <figcaption>{im.caption}</figcaption>}
              </figure>
            ))}
          </div>
        );
      }
      case "BOOKING_CTA":
        return unitsList.length > 0 ? (
          <div style={{ textAlign: "center" }}>
            <button type="button" className="ps-btn ps-btn-primary" onClick={() => handleOpenBooking(unitsList[0])}>
              {t("publicsite.booking.nav.bookDirect")}
            </button>
          </div>
        ) : null;
      default:
        return (
          <>
            {sectionImg && (
              <div className="ps-section-image">
                <img src={sectionImg} alt={sec.title || ""} />
              </div>
            )}
            {sec.content && <div className="ps-prose">{sec.content}</div>}
            {sec.items && sec.items.length > 0 && (
              <div className="ps-grid" style={{ marginTop: sec.content ? 28 : 0 }}>
                {sec.items.map((it, idx) => {
                  const img = safeImageUrl(it.image_url);
                  return (
                    <div key={idx} className="ps-card ps-amenity">
                      {img && <img src={img} alt={String(it.name || "")} />}
                      <h4>{String(it.name || it.q || "")}</h4>
                      <p>{String(it.description || it.a || "")}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
    }
  };

  const navAnchor = (type: string) => type.toLowerCase();

  return (
    <div className="ps" data-theme={themeFromSettings(site.theme_settings)} style={accent ? ({ "--ps-accent": accent } as React.CSSProperties) : undefined}>
      {isPreview && (
        <div className="ps-preview-bar">
          {t("publicsite.booking.preview")}
          {backHref && (
            <>
              {" "}
              <a href={backHref}>{t("sites.editor.back")}</a>
            </>
          )}
        </div>
      )}

      <header className="ps-header">
        <div className="ps-brand">
          {logoUrl && <img src={logoUrl} alt={site.property.name} />}
          <span className="ps-brand-name">{site.property.name}</span>
        </div>
        <nav className="ps-nav">
          <span className="ps-nav-lang">
            <LanguageSwitcher />
          </span>
          {has("UNITS") && (
            <a className="ps-nav-link" href={`#${navAnchor("UNITS")}`}>{t("publicsite.booking.nav.units")}</a>
          )}
          {packages.length > 0 && (
            <a className="ps-nav-link" href={`#${navAnchor("PACKAGES")}`}>{t("publicsite.booking.packages.title")}</a>
          )}
          {has("AMENITIES") && (
            <a className="ps-nav-link" href={`#${navAnchor("AMENITIES")}`}>{t("publicsite.booking.nav.amenities")}</a>
          )}
          {has("ACTIVITIES") && (
            <a className="ps-nav-link" href={`#${navAnchor("ACTIVITIES")}`}>{t("publicsite.booking.nav.experiences")}</a>
          )}
          {unitsList.length > 0 && (
            <button type="button" onClick={() => handleOpenBooking(unitsList[0])} className="ps-btn ps-btn-primary ps-btn-sm">
              {t("publicsite.booking.nav.bookDirect")}
            </button>
          )}
        </nav>
      </header>

      <section className={`ps-hero${heroUrl ? " has-image" : ""}`}>
        {heroUrl && (
          <div className="ps-hero-media">
            <img src={heroUrl} alt="" />
          </div>
        )}
        <div className="ps-hero-inner">
          <div className="ps-badge">{"✦"} {t("publicsite.booking.hero.badge")}</div>
          <h1>{site.title}</h1>
          {site.description && <p className="ps-hero-desc">{site.description}</p>}

          {unitsList.length > 0 && (
            <div className="ps-search">
              <div className="ps-field ps-field-dates">
                <label>{t("publicsite.booking.dates.stay")}</label>
                <DateRangePicker checkIn={checkIn} checkOut={checkOut} maxDate={site.property.bookable_until} onChange={(a, b) => { setCheckIn(a); setCheckOut(b); }} />
              </div>
              <div className="ps-field">
                <label>{t("publicsite.booking.guests")}</label>
                <GuestStepper value={guestCount} onChange={(n) => { setAdults(n); setChildren(0); }} max={maxGuests} />
              </div>
              <a href={`#${navAnchor("UNITS")}`} className="ps-btn ps-btn-primary">
                {t("publicsite.booking.hero.viewUnits")} {"↓"}
              </a>
            </div>
          )}
        </div>
      </section>

      {sections.map((sec) => {
        const body = renderSectionBody(sec);
        if (body === null && !sec.title) return null;
        if (sec.type === "BOOKING_CTA") {
          return (
            <section key={sec.id} id={navAnchor(sec.type)} className="ps-section">
              <div className="ps-wrap">
                <div className="ps-cta">
                  <h2>{sec.title}</h2>
                  {sec.subtitle && <p>{sec.subtitle}</p>}
                  {body}
                </div>
              </div>
            </section>
          );
        }
        if (sec.type === "PACKAGES" && packages.length === 0) return null;
        if (sec.type === "ACTIVITIES" && activities.length === 0) return null;
        if (sec.type === "FOOD" && food.length === 0) return null;
        return (
          <section key={sec.id} id={navAnchor(sec.type)} className="ps-section">
            <div className="ps-wrap">
              <div className="ps-section-head">
                <h2>{sec.title || (sec.type === "UNITS" ? t("publicsite.booking.units.title") : "")}</h2>
                {sec.subtitle && <p>{sec.subtitle}</p>}
              </div>
              {body}
            </div>
          </section>
        );
      })}

      <footer className="ps-footer">
        <p>{t("publicsite.booking.footer.copyright", { year: new Date().getFullYear(), name: site.property.name })}</p>
        <p style={{ marginTop: 4 }}>{t("publicsite.booking.footer.powered")}</p>
        {!isPreview && (
          <p style={{ marginTop: 4 }}>
            <button type="button" className="ps-link-btn" onClick={tracking.openConsentSettings}>{t("publicsite.consent.settings")}</button>
          </p>
        )}
      </footer>

      <SiteSupportButton whatsapp={site.whatsapp} siteName={site.property.name} />
      {!isPreview && (
        <SiteChatWidget
          hostname={site.hostname}
          siteName={site.property.name}
          booking={bookingSuccess ? { reference: bookingSuccess.public_reference, accessToken: bookingSuccess.access_token } : null}
          offsetBottom={site.whatsapp ? 84 : 16}
        />
      )}

      {detailUnit && (
        <div className="ps-modal-back" onClick={() => setDetailUnit(null)}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-modal-head">
              <div>
                <h3>{detailUnit.name}</h3>
                <p className="ps-muted">{t("publicsite.booking.units.capacity", { count: detailUnit.capacity })}</p>
                {perGuestNote(detailUnit) && <p className="ps-muted">{perGuestNote(detailUnit)}</p>}
                {poolNote(detailUnit) && <p className="ps-muted">{poolNote(detailUnit)}</p>}
              </div>
              <button type="button" className="ps-x" onClick={() => setDetailUnit(null)} aria-label={t("publicsite.booking.unit.close")}>{"✕"}</button>
            </div>
            <div className="ps-form">
              <div className="ps-detail-photo">
                <UnitPhotos images={detailUnit.images && detailUnit.images.length > 0 ? detailUnit.images : detailUnit.primary_image_url ? [detailUnit.primary_image_url] : []} videos={detailUnit.videos} name={detailUnit.name} />
              </div>
              {detailUnit.description && <p style={{ whiteSpace: "pre-line" }}>{detailUnit.description}</p>}
              {detailUnit.amenities && detailUnit.amenities.length > 0 && (
                <div>
                  <strong>{t("publicsite.booking.unit.amenities")}</strong>
                  <div className="ps-chips">{detailUnit.amenities.map((a) => <span key={a} className="ps-chip">{a}</span>)}</div>
                </div>
              )}
              <div className="ps-actions ps-detail-foot">
                <div>
                  <span className="ps-price">{money(detailUnit.base_price, detailUnit.currency)}</span> <span className="ps-muted">{t("publicsite.booking.units.perNight")}</span>
                  {nights > 0 && detailUnit.available !== false && (
                    <div className="ps-muted">{t("publicsite.booking.units.totalNights", { amount: money(stayPrice(detailUnit), detailUnit.currency), nights: t("publicsite.booking.nights", { count: nights }) })}</div>
                  )}
                </div>
                {detailUnit.available === false ? (
                  <span className="ps-avail no">{t("publicsite.booking.units.booked")}</span>
                ) : (
                  <button type="button" className="ps-btn ps-btn-primary" onClick={() => handleOpenBooking(detailUnit)}>{t("publicsite.booking.unit.bookThis")}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {dayTripActivity && (
        <PublicActivityBookingModal hostname={site.hostname} activity={dayTripActivity} onClose={() => setDayTripActivity(null)} />
      )}

      {isBookingModalOpen && selectedUnit && (
        <div className="ps-modal-back">
          <div className="ps-modal">
            <div className="ps-modal-head">
              <div>
                <h3>{t("publicsite.booking.modal.reserve", { name: selectedUnit.name })}</h3>
                <p className="ps-muted">{site.property.name}</p>
              </div>
              <button type="button" className="ps-x" onClick={() => setIsBookingModalOpen(false)} aria-label={t("publicsite.booking.success.close")}>
                {"✕"}
              </button>
            </div>

            {bookingSuccess ? (
              <div className="ps-success">
                <div style={{ fontSize: 32 }}>{"🎉"}</div>
                <div>
                  <span className="ps-tag">
                    {bookingSuccess.status === "PENDING" ? (bookingSuccess.online_payment_enabled ? t("publicsite.booking.success.awaitingPayment") : t("publicsite.booking.success.awaitingConfirmation")) : bookingSuccess.status}
                  </span>
                  <h4 style={{ marginTop: 6 }}>{t("publicsite.booking.success.title")}</h4>
                </div>
                <p>
                  {t("publicsite.booking.success.thanks", { name: bookingSuccess.guest_name })}{" "}
                  {t("publicsite.booking.success.created", { unit: bookingSuccess.unit_name, checkIn: formatDate(bookingSuccess.check_in), checkOut: formatDate(bookingSuccess.check_out), nights: t("publicsite.booking.nights", { count: bookingSuccess.nights }) })}
                </p>
                {guestEmail.trim() && <p className="ps-muted">{"✉"} {t("publicsite.booking.success.emailSent", { email: guestEmail.trim() })}</p>}
                <div className="ps-box" style={{ textAlign: "left" }}>
                  <div className="line"><span className="ps-muted">{t("publicsite.booking.success.reference")}</span><strong>{bookingSuccess.public_reference}</strong></div>
                  <div className="line"><span className="ps-muted">{t("publicsite.booking.success.property")}</span><span>{bookingSuccess.property_name}</span></div>
                  <div className="line"><span className="ps-muted">{t("publicsite.booking.totalRate")}</span><strong>{formatMoney(bookingSuccess.total, bookingSuccess.currency)}</strong></div>
                </div>
                {bookingSuccess.online_payment_enabled ? (
                  <div className="ps-box" style={{ textAlign: "left" }}>
                    <div className="line">
                      <strong>{"💳"} {t("publicsite.booking.success.securePayment")}</strong>
                      <span className="ps-tag">{t("publicsite.booking.success.methods")}</span>
                    </div>
                    <p className="ps-muted">{t("publicsite.booking.success.payHint")}</p>
                    {payError && <p role="alert" style={{ color: "#b42318", fontSize: 13 }}>{payError}</p>}
                    <button type="button" onClick={handlePayNow} disabled={payLoading} className="ps-btn ps-btn-primary">
                      {payLoading ? t("publicsite.booking.success.payLoading") : t("publicsite.booking.success.payNow")}
                    </button>
                  </div>
                ) : (
                  <div className="ps-box" style={{ textAlign: "left" }}>
                    <strong>{t("publicsite.booking.success.confirmTitle")}</strong>
                    <p className="ps-muted">{t("publicsite.booking.success.confirmHint")}</p>
                  </div>
                )}
                {bookingSuccess.online_payment_enabled && <p className="ps-muted">{"ℹ"} {t("publicsite.booking.success.nextStep")}</p>}
                <button type="button" onClick={() => setIsBookingModalOpen(false)} className="ps-btn ps-btn-ghost">
                  {t("publicsite.booking.success.close")}
                </button>
              </div>
            ) : checkoutStep === "DETAILS" ? (
              <form onSubmit={handleProceedToReview} className="ps-form">
                {bookingError && <div className="ps-error">{bookingError}</div>}

                <div>
                  <label className="ps-lbl">{t("publicsite.booking.dates.stay")}</label>
                  <DateRangePicker
                    checkIn={checkIn}
                    checkOut={checkOut}
                    fixedNights={selectedPackage ? selectedPackage.nights : undefined}
                    maxDate={site.property.bookable_until}
                    onChange={(a, b) => { setCheckIn(a); setCheckOut(b); }}
                  />
                </div>
                <p className="ps-muted" style={{ marginTop: -6, fontSize: 12 }}>
                  {t("publicsite.booking.form.times", { checkIn: site.property.check_in_time, checkOut: site.property.check_out_time })}
                </p>

                {packagesFor(selectedUnit.id).length > 0 && (
                  <div>
                    <label className="ps-lbl">{t("publicsite.booking.packages.label")}</label>
                    <select className="ps-select" value={selectedPackageId} onChange={(e) => setSelectedPackageId(e.target.value)}>
                      <option value="">{t("publicsite.booking.packages.none")}</option>
                      {packagesFor(selectedUnit.id).map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} · {t("publicsite.booking.packages.nights", { count: pkg.nights })} · {money(pkg.price, pkg.currency)}
                        </option>
                      ))}
                    </select>
                    {selectedPackage && <p className="ps-muted" style={{ marginTop: 4, fontSize: 12 }}>{t("publicsite.booking.packages.fixed", { count: selectedPackage.nights })}</p>}
                  </div>
                )}

                <div>
                  {selectedUnit.kind === "POOL" && (
                    <div style={{ marginBottom: 10 }}>
                      <label className="ps-lbl">{t("publicsite.booking.form.plotCount")}</label>
                      <input
                        type="number"
                        className="ps-input"
                        min={1}
                        max={selectedUnit.available_count ?? selectedUnit.total_count ?? 1}
                        value={plotCount}
                        onChange={(e) => setPlotCount(Number(e.target.value))}
                        required
                      />
                      <p className="ps-muted" style={{ marginTop: 4, fontSize: 12 }}>{t("publicsite.booking.form.plotCountHint", { capacity: selectedUnit.capacity })}</p>
                    </div>
                  )}
                  <div className="ps-row2">
                    <div>
                      <label className="ps-lbl">{t("publicsite.booking.form.adults")}</label>
                      <input type="number" className="ps-input" min={1} max={Math.max(1, selectedUnit.capacity * plots - (children || 0))} value={adults} onChange={(e) => setAdults(Number(e.target.value))} required />
                    </div>
                    <div>
                      <label className="ps-lbl">{t("publicsite.booking.form.children")}</label>
                      <input type="number" className="ps-input" min={0} max={Math.max(0, selectedUnit.capacity * plots - (adults || 1))} value={children} onChange={(e) => setChildren(Number(e.target.value))} />
                    </div>
                  </div>
                  {perGuestNote(selectedUnit) && <p className="ps-muted" style={{ marginTop: 4 }}>{perGuestNote(selectedUnit)}</p>}
                </div>

                <div>
                  <label className="ps-lbl">{t("publicsite.booking.form.fullName")}</label>
                  <input type="text" className="ps-input" placeholder={t("publicsite.booking.form.fullNamePlaceholder")} value={guestName} onChange={(e) => setGuestName(e.target.value)} required />
                </div>

                <div className="ps-row2">
                  <div>
                    <label className="ps-lbl">{t("publicsite.booking.form.phone")}</label>
                    <input type="tel" inputMode="tel" autoComplete="tel" className="ps-input" placeholder="+62 812..." value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} required />
                  </div>
                  <div>
                    <label className="ps-lbl">{t("publicsite.booking.form.email")}</label>
                    <input type="email" inputMode="email" autoComplete="email" className="ps-input" placeholder="guest@example.com" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{t("publicsite.booking.form.emailHint")}</div>
                  </div>
                </div>

                <div>
                  <label className="ps-lbl">{t("publicsite.booking.form.notes")}</label>
                  <textarea className="ps-textarea" placeholder={t("publicsite.booking.form.notesPlaceholder")} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>

                {addons.length > 0 && (
                  <div>
                    <label className="ps-lbl">{t("publicsite.booking.addons.title")} <span className="ps-muted">({t("publicsite.booking.voucher.optional")})</span></label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {addons.map((a) => {
                        const picked = addonQty[a.id] !== undefined;
                        return (
                          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <input
                              type="checkbox"
                              id={`addon-${a.id}`}
                              checked={picked}
                              onChange={(e) => {
                                const next = { ...addonQty };
                                if (e.target.checked) next[a.id] = 1;
                                else delete next[a.id];
                                setAddonQty(next);
                              }}
                            />
                            <label htmlFor={`addon-${a.id}`} style={{ flex: 1, cursor: "pointer" }}>
                              {a.name}
                              <span className="ps-muted" style={{ display: "block", fontSize: 12 }}>{tx(`publicsite.booking.addons.${a.basis}`, { price: money(a.price, a.currency) })}</span>
                            </label>
                            {picked && a.basis === "QUANTITY" && (
                              <input
                                type="number"
                                className="ps-input"
                                style={{ width: 72 }}
                                min={1}
                                max={99}
                                aria-label={a.name}
                                value={addonQty[a.id]}
                                onChange={(e) => setAddonQty({ ...addonQty, [a.id]: Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)) })}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="ps-muted" style={{ marginTop: 4, fontSize: 12 }}>{t("publicsite.booking.addons.hint")}</p>
                  </div>
                )}

                <div>
                  <label className="ps-lbl">{t("publicsite.booking.voucher.label")} <span className="ps-muted">({t("publicsite.booking.voucher.optional")})</span></label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      className="ps-input"
                      style={{ textTransform: "uppercase", flex: 1 }}
                      maxLength={24}
                      autoComplete="off"
                      placeholder={t("publicsite.booking.voucher.placeholder")}
                      value={voucherInput}
                      onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setVoucherCode(voucherInput.trim());
                        }
                      }}
                      disabled={Boolean(voucherCode)}
                    />
                    {voucherCode ? (
                      <button type="button" className="ps-btn ps-btn-ghost" onClick={() => { setVoucherCode(""); setVoucherInput(""); }}>
                        {t("publicsite.booking.voucher.remove")}
                      </button>
                    ) : (
                      <button type="button" className="ps-btn ps-btn-ghost" disabled={!voucherInput.trim()} onClick={() => setVoucherCode(voucherInput.trim())}>
                        {t("publicsite.booking.voucher.apply")}
                      </button>
                    )}
                  </div>
                  {freshQuote?.voucher && (
                    <p style={{ marginTop: 4, fontSize: 12, color: "var(--ps-success, #0f5132)" }}>
                      {t("publicsite.booking.voucher.applied", { code: freshQuote.voucher.code, amount: money(freshQuote.discount, freshQuote.currency) })}
                    </p>
                  )}
                  {freshQuote?.voucher_error && (
                    <p style={{ marginTop: 4, fontSize: 12, color: "var(--ps-danger)" }}>
                      {localizeError(locale, freshQuote.voucher_error.code, {
                        min_nights: freshQuote.voucher_error.min_nights ?? 0,
                        min_spend: money(freshQuote.voucher_error.min_spend ?? 0, freshQuote.currency),
                      })}
                    </p>
                  )}
                </div>

                {nights > 0 && (
                  <div className={`ps-quote${freshQuote ? (freshQuote.available ? " ok" : " no") : ""}`} aria-live="polite">
                    {isPreview ? null : !freshQuote ? (
                      t("publicsite.booking.quote.checking")
                    ) : freshQuote.available ? (
                      t("publicsite.booking.quote.available")
                    ) : (
                      <>
                        <strong>{t("publicsite.booking.quote.unavailableTitle")}:</strong> {localizeError(locale, `${freshQuote.reason}: `)}
                        {freshQuote.reason === "CONFLICTING_RESERVATION" && alternatives.length > 0 && (
                          <>
                            <div style={{ marginTop: 6 }}>{t("publicsite.booking.quote.alternatives")}</div>
                            <div className="ps-alt">
                              {alternatives.map((u) => (
                                <button key={u.id} type="button" className="ps-btn ps-btn-ghost ps-btn-sm" onClick={() => switchUnit(u)}>
                                  {t("publicsite.booking.quote.useUnit", { name: u.name })}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="ps-box">
                  <div className="line"><span className="ps-muted">{t("publicsite.booking.form.stayDuration")}</span><span>{t("publicsite.booking.nights", { count: nights })}</span></div>
                  {freshQuote && (freshQuote.discount > 0 || freshQuote.charges.length > 0 || freshQuote.addons.length > 0) && (
                    <>
                      <div className="line"><span className="ps-muted">{t("publicsite.booking.voucher.subtotal")}</span><span>{money(freshQuote.subtotal, selectedUnit.currency)}</span></div>
                      {freshQuote.discount > 0 && (
                        <div className="line"><span className="ps-muted">{t("publicsite.booking.voucher.discount")}</span><span>{"−"}{money(freshQuote.discount, selectedUnit.currency)}</span></div>
                      )}
                      {freshQuote.addons.map((a) => (
                        <div className="line" key={a.catalog_item_id}>
                          <span className="ps-muted">{t("publicsite.booking.addons.line", { name: a.name, count: a.units })}</span>
                          <span>{"+"}{money(a.amount, selectedUnit.currency)}</span>
                        </div>
                      ))}
                      {freshQuote.charges.map((c) => (
                        <div className="line" key={c.name}>
                          <span className="ps-muted">{c.inclusive ? t("charges.included", { name: c.name, percent: c.rate_bps / 100 }) : t("charges.line", { name: c.name, percent: c.rate_bps / 100 })}</span>
                          <span>{c.inclusive ? "" : "+"}{money(c.amount, selectedUnit.currency)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="line total"><span>{t("publicsite.booking.totalRate")}</span><span style={{ color: "var(--ps-accent)" }}>{money(displayTotal, selectedUnit.currency)}</span></div>
                </div>

                <div className="ps-actions">
                  <button type="button" onClick={() => setIsBookingModalOpen(false)} className="ps-btn ps-btn-ghost">
                    {t("publicsite.booking.form.cancel")}
                  </button>
                  <button type="submit" disabled={nights <= 0 || (!isPreview && (quoteLoading || (freshQuote ? !freshQuote.available || Boolean(freshQuote.voucher_error) : false)))} className="ps-btn ps-btn-primary">
                    {t("publicsite.booking.form.review")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="ps-form">
                {bookingError && <div className="ps-error">{bookingError}</div>}

                <div className="ps-box">
                  <strong>{t("publicsite.booking.review.title")}</strong>
                  <div className="ps-row2">
                    <div><span className="ps-muted" style={{ display: "block" }}>{t("publicsite.booking.review.property")}</span>{site.property.name}</div>
                    <div><span className="ps-muted" style={{ display: "block" }}>{t("publicsite.booking.review.unit")}</span>{selectedUnit.name}{plots > 1 ? ` × ${plots}` : ""}{selectedPackage ? ` · ${selectedPackage.name}` : ""}</div>
                    <div><span className="ps-muted" style={{ display: "block" }}>{t("publicsite.booking.review.stayDates")}</span>{formatDate(checkIn)} {"→"} {formatDate(checkOut)} ({t("publicsite.booking.nights", { count: nights })})</div>
                    <div><span className="ps-muted" style={{ display: "block" }}>{t("publicsite.booking.review.guestDetails")}</span>{guestName} ({children > 0 ? t("publicsite.booking.adultsChildren", { adults, children }) : t("publicsite.booking.guestCount", { count: guestCount })})</div>
                  </div>
                  {freshQuote && freshQuote.discount > 0 && (
                    <>
                      <div className="line"><span className="ps-muted">{t("publicsite.booking.voucher.subtotal")}</span><span>{money(freshQuote.subtotal, selectedUnit.currency)}</span></div>
                      <div className="line"><span className="ps-muted">{t("publicsite.booking.voucher.discount")} ({freshQuote.voucher?.code})</span><span>{"−"}{money(freshQuote.discount, selectedUnit.currency)}</span></div>
                    </>
                  )}
                  {freshQuote?.addons.map((a) => (
                    <div className="line" key={a.catalog_item_id}>
                      <span className="ps-muted">{t("publicsite.booking.addons.line", { name: a.name, count: a.units })}</span>
                      <span>{"+"}{money(a.amount, selectedUnit.currency)}</span>
                    </div>
                  ))}
                  <div className="line total"><span>{t("publicsite.booking.review.totalPrice")}</span><span style={{ color: "var(--ps-accent)" }}>{money(displayTotal, selectedUnit.currency)}</span></div>
                </div>

                <div className="ps-actions" style={{ justifyContent: "space-between" }}>
                  <button type="button" onClick={() => setCheckoutStep("DETAILS")} className="ps-btn ps-btn-ghost" disabled={bookingLoading}>
                    {t("publicsite.booking.review.back")}
                  </button>
                  <button type="button" onClick={handleBookingSubmit} disabled={bookingLoading} className="ps-btn ps-btn-primary">
                    {bookingLoading ? t("publicsite.booking.review.creating") : t("publicsite.booking.review.confirm")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
